import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Navbar, Nav, Container, NavDropdown } from "react-bootstrap";
import { useAuth0 } from "@auth0/auth0-react";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { FaShoppingCart, FaUser } from "react-icons/fa";

export const Navigation: React.FC = () => {
  const { isAuthenticated, logout, loginWithRedirect, user } = useAuth0();
  const { openCart, cartQuantity } = useShoppingCart();
  const location = useLocation();

  const [expanded, setExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const navRef = useRef<HTMLDivElement | null>(null);

  // Get user's initials from name or email
  const getUserInitials = () => {
    if (user?.name) {
      const nameParts = user.name.trim().split(/\s+/);
      if (nameParts.length >= 2) {
        // First and last initial
        return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
      }
      // Only one name part, return first initial
      return nameParts[0].charAt(0).toUpperCase();
    }
    if (user?.email) {
      // For email, just return first character
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  // Reset image error state when user changes
  useEffect(() => {
    setImageError(false);
  }, [user?.picture]);

  const handleLogout = () =>
    logout({ logoutParams: { returnTo: window.location.origin } });

  // 1) Close menu on route change (navigating via Links or programmatically)
  useEffect(() => {
    setExpanded(false);
  }, [location.pathname]);

  // 2) Close when clicking outside the navbar while expanded
  useEffect(() => {
    if (!expanded) return;
    const onDocClick = (e: MouseEvent) => {
      if (!navRef.current) return;
      if (!navRef.current.contains(e.target as Node)) setExpanded(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [expanded]);

  // 3) Close when navbar loses focus entirely (keyboard tabbing, etc.)
  const handleBlurCapture: React.FocusEventHandler<HTMLDivElement> = (e) => {
    // allow focus to settle, then check if focus left the navbar
    setTimeout(() => {
      if (!navRef.current) return;
      const active = document.activeElement;
      if (active && !navRef.current.contains(active)) setExpanded(false);
    }, 0);
  };

  const close = () => setExpanded(false);

  return (
    <Navbar
      ref={navRef as any}
      bg="dark"
      variant="dark"
      expand="lg"
      className="border-bottom"
      collapseOnSelect
      expanded={expanded}
      onToggle={setExpanded}
      onBlurCapture={handleBlurCapture}
    >
      <Container>
        <Navbar.Brand as={Link} to="/" onClick={close}>
          <img
            src="/logo-wide.png"
            alt="Wallscapes.AI Logo"
            style={{ height: "60px", marginRight: "10px" }}
          />
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="basic-navbar-nav" />

        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto align-items-center">
            <Nav.Link as={Link} to="/products" onClick={close}>
              Shop
            </Nav.Link>
            <Nav.Link as={Link} to="/products/create" onClick={close}>
              Create
            </Nav.Link>

            {isAuthenticated ? (
              <>
                <Nav.Link as={Link} to="/designs" onClick={close} className="me-2">
                  My Designs
                </Nav.Link>

                {/* Profile Dropdown */}
                <NavDropdown
                  title={
                    <div className="d-inline-flex align-items-center">
                      {user?.picture && !imageError ? (
                        <img
                          src={user.picture}
                          alt="Profile"
                          className="rounded-circle"
                          style={{
                            width: "32px",
                            height: "32px",
                            objectFit: "cover",
                            border: "2px solid #fff",
                          }}
                          onError={() => setImageError(true)}
                        />
                      ) : (
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                          style={{
                            width: "32px",
                            height: "32px",
                            fontWeight: "bold",
                            fontSize: "0.75rem",
                            border: "2px solid #fff",
                          }}
                        >
                          {getUserInitials()}
                        </div>
                      )}
                    </div>
                  }
                  id="profile-dropdown"
                  align="end"
                  className="profile-dropdown me-1"
                >
                  <NavDropdown.Item as={Link} to="/account" onClick={close}>
                    <FaUser className="me-2" />
                    My Account
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item
                    onClick={() => {
                      close();
                      handleLogout();
                    }}
                  >
                    Sign Out
                  </NavDropdown.Item>
                </NavDropdown>
              </>
            ) : (
              <Nav.Link
                onClick={() => {
                  close();
                  loginWithRedirect();
                }}
              >
                Login
              </Nav.Link>
            )}

            {/* Cart - also close menu when opening cart */}
            <Nav.Link
              onClick={() => {
                close();
                openCart();
              }}
              className="d-flex align-items-center position-relative"
              title="View Shopping Cart"
            >
              <FaShoppingCart size={22} />
              {cartQuantity > 0 && (
                <div
                  className="rounded-circle bg-danger d-flex justify-content-center align-items-center"
                  style={{
                    color: "white",
                    width: "1rem",
                    height: "1rem",
                    position: "absolute",
                    top: "2px",
                    right: "2px",
                    fontSize: "0.65rem",
                    lineHeight: "1rem",
                  }}
                >
                  {cartQuantity}
                </div>
              )}
            </Nav.Link>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};
